import type { SerializedMeterModel } from '@enmon/commons/lib/tools';
import type { TableColumnsType } from 'antd';
import { EmptyCell, Tag } from 'components';
import { Link } from 'react-router-dom';
import { Icon } from 'components/icon';
import { isEmpty } from 'ramda';
import { commoditiesConfig } from '@/utils/commodities';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import { ActionButton } from '../../../../../components/button/Action';
import type { SerializedMeterModelWithCount } from '@/lib/services';

export const getModelsColumnsConfig = ({
  loading,
  isMagicRole,
  onClick,
  onDelete,
  sortState,
}: {
  loading: boolean;
  isMagicRole: boolean;
  onClick: (modelId: SerializedMeterModel['id']) => void;
  onDelete: (modelId: SerializedMeterModel['id']) => void;
  sortState: { field: Key | null; order: SortOrder };
}): TableColumnsType<SerializedMeterModelWithCount> => [
  {
    key: 'commodity',
    title: 'Commodity',
    width: 100,
    render: (_, record) => {
      const iconString = commoditiesConfig[record.commodity]?.iconString;
      return iconString ? <Icon iconString={iconString} /> : <EmptyCell />;
    },
  },
  {
    key: 'name',
    title: 'Name',
    sorter: true,
    sortOrder: sortState.field === 'name' ? sortState.order : null,
    width: 300,
    render: (_, record) => <Link to={{ pathname: `model/${record.id}` }}>{record.name}</Link>,
  },
  {
    key: 'manufacturer.name',
    title: 'Manufacturer',
    sorter: true,
    sortOrder: sortState.field === 'manufacturer.name' ? sortState.order : null,
    width: 300,
    render: (_, record) => <>{record.manufacturer?.name ?? <EmptyCell />}</>,
  },
  {
    key: 'technicalData',
    title: 'Technical data',
    width: 150,
    render: (_, record) => {
      const isDetailsEmpty = isEmpty(record.details);
      return <Tag color={isDetailsEmpty ? 'error' : 'success'}>{isDetailsEmpty ? 'No' : 'Yes'}</Tag>;
    },
  },
  {
    key: 'metersCount',
    title: 'Meters count',
    sorter: true,
    sortOrder: sortState.field === 'metersCount' ? sortState.order : null,
    width: 120,
    render: (_, record) => record.metersCount ?? <EmptyCell />,
  },
  ...(isMagicRole
    ? ([
        {
          key: 'action',
          width: 150,
          title: 'Actions',
          fixed: 'right',
          render: (_, record) => (
            <ActionButton isLoading={loading} onClick={() => onClick(record.id)} onDelete={() => onDelete(record.id)} />
          ),
        },
      ] satisfies TableColumnsType<SerializedMeterModel>)
    : []),
];
